/**
 * Timeline API Service
 *
 * API service for task and order timeline operations
 * Based on backend endpoints defined in Phase 1
 */

import api from './api';
import {
  TaskTimelineResponse,
  TaskProcessingFlowResponse,
  OrderTimelineResponse,
  TaskTimelineEvent,
  TaskStop,
  TaskPhoto,
  TaskStatus,
  TransitionStatusRequest,
  TimeRecordRequest,
  PauseTaskRequest,
  StopRequest,
  PhotoUploadRequest,
} from '../types/timeline';

// ==================== Task Timeline API ====================

export const timelineApi = {
  // ==================== Status Transitions ====================

  /**
   * Transition task to a new status with validation
   * POST /api/tasks/:id/transition
   */
  transitionStatus: async (
    taskId: number,
    data: TransitionStatusRequest
  ): Promise<{ task: Record<string, unknown>; event: TaskTimelineEvent }> => {
    const response = await api.post(`/tasks/${taskId}/transition`, data);
    return response.data;
  },

  /**
   * Get valid next statuses for a task
   * GET /api/tasks/:id/next-statuses
   */
  getNextStatuses: async (taskId: number): Promise<TaskStatus[]> => {
    const response = await api.get(`/tasks/${taskId}/next-statuses`);
    return response.data;
  },

  // ==================== Terminal Time Recording ====================

  /**
   * Record entering terminal (first visit)
   * POST /api/tasks/:id/enter-terminal
   */
  enterTerminal: async (
    taskId: number,
    data: TimeRecordRequest = {}
  ): Promise<{ task: Record<string, unknown>; event: TaskTimelineEvent }> => {
    const response = await api.post(`/tasks/${taskId}/enter-terminal`, data);
    return response.data;
  },

  /**
   * Record exiting terminal (first visit)
   * POST /api/tasks/:id/exit-terminal
   */
  exitTerminal: async (
    taskId: number,
    data: TimeRecordRequest = {}
  ): Promise<{ task: Record<string, unknown>; event: TaskTimelineEvent }> => {
    const response = await api.post(`/tasks/${taskId}/exit-terminal`, data);
    return response.data;
  },

  /**
   * Record entering terminal (second visit - Liveload return)
   * POST /api/tasks/:id/enter-terminal-return
   */
  enterTerminalReturn: async (
    taskId: number,
    data: TimeRecordRequest = {}
  ): Promise<{ task: Record<string, unknown>; event: TaskTimelineEvent }> => {
    const response = await api.post(`/tasks/${taskId}/enter-terminal-return`, data);
    return response.data;
  },

  /**
   * Record exiting terminal (second visit - Liveload return)
   * POST /api/tasks/:id/exit-terminal-return
   */
  exitTerminalReturn: async (
    taskId: number,
    data: TimeRecordRequest = {}
  ): Promise<{ task: Record<string, unknown>; event: TaskTimelineEvent }> => {
    const response = await api.post(`/tasks/${taskId}/exit-terminal-return`, data);
    return response.data;
  },

  // ==================== Warehouse Time Recording ====================

  /**
   * Record arriving at warehouse (starts detention timer)
   * POST /api/tasks/:id/arrive-warehouse
   */
  arriveWarehouse: async (
    taskId: number,
    data: TimeRecordRequest = {}
  ): Promise<{ task: Record<string, unknown>; event: TaskTimelineEvent }> => {
    const response = await api.post(`/tasks/${taskId}/arrive-warehouse`, data);
    return response.data;
  },

  /**
   * Record going on dock at warehouse
   * POST /api/tasks/:id/on-dock
   */
  onDock: async (
    taskId: number,
    data: TimeRecordRequest = {}
  ): Promise<{ task: Record<string, unknown>; event: TaskTimelineEvent }> => {
    const response = await api.post(`/tasks/${taskId}/on-dock`, data);
    return response.data;
  },

  /**
   * Record container emptied (Import unload)
   * POST /api/tasks/:id/empty
   */
  recordEmpty: async (
    taskId: number,
    data: TimeRecordRequest = {}
  ): Promise<{ task: Record<string, unknown>; event: TaskTimelineEvent }> => {
    const response = await api.post(`/tasks/${taskId}/empty`, data);
    return response.data;
  },

  /**
   * Record container loaded (Export load)
   * POST /api/tasks/:id/loaded
   */
  recordLoaded: async (
    taskId: number,
    data: TimeRecordRequest = {}
  ): Promise<{ task: Record<string, unknown>; event: TaskTimelineEvent }> => {
    const response = await api.post(`/tasks/${taskId}/loaded`, data);
    return response.data;
  },

  /**
   * Record exiting warehouse (ends detention timer)
   * POST /api/tasks/:id/exit-warehouse
   */
  exitWarehouse: async (
    taskId: number,
    data: TimeRecordRequest = {}
  ): Promise<{ task: Record<string, unknown>; event: TaskTimelineEvent }> => {
    const response = await api.post(`/tasks/${taskId}/exit-warehouse`, data);
    return response.data;
  },

  // ==================== Pause/Resume ====================

  /**
   * Pause task
   * POST /api/tasks/:id/pause
   */
  pauseTask: async (
    taskId: number,
    data: PauseTaskRequest
  ): Promise<{ task: Record<string, unknown>; event: TaskTimelineEvent }> => {
    const response = await api.post(`/tasks/${taskId}/pause`, data);
    return response.data;
  },

  /**
   * Resume task
   * POST /api/tasks/:id/resume
   */
  resumeTask: async (
    taskId: number,
    data: TimeRecordRequest = {}
  ): Promise<{ task: Record<string, unknown>; event: TaskTimelineEvent }> => {
    const response = await api.post(`/tasks/${taskId}/resume`, data);
    return response.data;
  },

  // ==================== Stop Management ====================

  /**
   * Record entering a stop
   * POST /api/tasks/:id/stops
   */
  enterStop: async (
    taskId: number,
    data: StopRequest = {}
  ): Promise<{ task: Record<string, unknown>; stop: TaskStop }> => {
    const response = await api.post(`/tasks/${taskId}/stops`, data);
    return response.data;
  },

  /**
   * Record leaving a stop
   * POST /api/tasks/:id/stops/:stopId/leave
   */
  leaveStop: async (
    taskId: number,
    stopId: number,
    data: TimeRecordRequest = {}
  ): Promise<{ task: Record<string, unknown>; stop: TaskStop }> => {
    const response = await api.post(`/tasks/${taskId}/stops/${stopId}/leave`, data);
    return response.data;
  },

  /**
   * Get all stops for a task
   * GET /api/tasks/:id/stops
   */
  getStops: async (taskId: number): Promise<TaskStop[]> => {
    const response = await api.get(`/tasks/${taskId}/stops`);
    return response.data;
  },

  // ==================== Timeline ====================

  /**
   * Get complete task timeline
   * GET /api/tasks/:id/timeline
   */
  getTaskTimeline: async (taskId: number | string): Promise<TaskTimelineResponse> => {
    const response = await api.get(`/tasks/${taskId}/timeline`);
    return response.data;
  },

  /**
   * Get task processing flow (template + recorded events)
   * GET /api/tasks/:id/processing-flow
   */
  getTaskProcessingFlow: async (taskId: number | string): Promise<TaskProcessingFlowResponse> => {
    const response = await api.get(`/tasks/${taskId}/processing-flow`);
    return response.data;
  },

  // ==================== Photos ====================

  /**
   * Upload photo for task
   * POST /api/tasks/:id/photos
   */
  uploadPhoto: async (
    taskId: number,
    data: PhotoUploadRequest
  ): Promise<TaskPhoto> => {
    const response = await api.post(`/tasks/${taskId}/photos`, data);
    return response.data;
  },

  /**
   * Get all photos for a task
   * GET /api/tasks/:id/photos
   */
  getPhotos: async (taskId: number, photoType?: string): Promise<TaskPhoto[]> => {
    const params = photoType ? `?photo_type=${photoType}` : '';
    const response = await api.get(`/tasks/${taskId}/photos${params}`);
    return response.data;
  },
};

// ==================== Order Timeline API (6-step progress) ====================

export const orderTimelineApi = {
  /**
   * Get order timeline with all 6 steps
   * GET /api/orders/:orderId/timeline
   */
  getOrderTimeline: async (orderId: number | string): Promise<OrderTimelineResponse> => {
    const response = await api.get(`/orders/${orderId}/timeline`);
    return response.data;
  },

  /**
   * Update order timeline step
   * PATCH /api/orders/:orderId/timeline/:stepName
   */
  updateTimelineStep: async (
    orderId: number | string,
    stepName: string,
    data: { status?: string; notes?: string }
  ): Promise<Record<string, unknown>> => {
    const response = await api.patch(`/orders/${orderId}/timeline/${stepName}`, data);
    return response.data;
  },
};

